import React, { useEffect, useMemo, useState } from "react";
import { FiDownload, FiTrash2, FiLoader, FiMic, FiInfo, FiCopy, FiCheck, FiGlobe, FiFileText } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { UploadCard, useJobHistory, formatSeconds } from "./audioShared";

// Same list as the server (core/transcribe.py LANGUAGES) - "auto" lets the Whisper model detect the language.
const LANGUAGES = [
  ["auto", "Detect automatically"],
  ["en", "English"], ["ur", "Urdu"], ["hi", "Hindi"], ["pa", "Punjabi"], ["ar", "Arabic"],
  ["fa", "Persian"], ["tr", "Turkish"], ["bn", "Bengali"], ["id", "Indonesian"],
  ["fr", "French"], ["de", "German"], ["es", "Spanish"], ["pt", "Portuguese"], ["it", "Italian"],
  ["nl", "Dutch"], ["pl", "Polish"], ["el", "Greek"], ["ja", "Japanese"], ["ko", "Korean"],
  ["zh", "Chinese"], ["vi", "Vietnamese"], ["ru", "Russian"],
];
const LANGUAGE_NAME = Object.fromEntries(LANGUAGES);
const TRANSLATE_TO = [
  ["en", "English"], ["ur", "Urdu"], ["hi", "Hindi"], ["pa", "Punjabi"], ["ar", "Arabic"], ["fa", "Persian"],
  ["tr", "Turkish"], ["bn", "Bengali"], ["fr", "French"], ["de", "German"], ["es", "Spanish"], ["pt", "Portuguese"],
  ["it", "Italian"], ["ru", "Russian"], ["zh-CN", "Chinese"], ["ja", "Japanese"], ["ko", "Korean"], ["id", "Indonesian"],
];
const MAX_AUDIO_BYTES = 80 * 1024 * 1024;
const RTL_LANGUAGES = ["ur", "ar", "fa", "he", "ps", "sd"];

const selectClass = "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";

function stamp(seconds) {
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rest = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${rest}` : `${m}:${rest}`;
}

function download(filename, text) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/plain;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    return ok;
  }
}

function CopyButton({ getText, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => { if (await copyText(getText())) { setDone(true); setTimeout(() => setDone(false), 1800); } }}
      className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors"
    >
      {done ? <><FiCheck className="text-emerald-400" /> Copied</> : <><FiCopy /> {label}</>}
    </button>
  );
}

// AI Audio and Podcast Transcription: a recording in, its text out. The work runs on the server in the
// background (long recordings are cut into pieces) and this page polls for progress.
export default function TranscriptionTool() {
  const { clientId, history, loading, running, latest, elapsed, add, remove } = useJobHistory("transcription");
  const [audio, setAudio] = useState(null);
  const [language, setLanguage] = useState("auto");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fileError, setFileError] = useState("");

  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [timestamps, setTimestamps] = useState(true);
  const [target, setTarget] = useState("en");
  const [translating, setTranslating] = useState(false);
  const [translated, setTranslated] = useState(null);   // { target, paragraphs }
  const [translateError, setTranslateError] = useState("");

  const canSubmit = audio && !submitting && !running;

  // Show the newest finished transcript automatically.
  useEffect(() => {
    if (latest && latest.status === "completed" && latest.has_transcript) setViewId(latest.id);
  }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    setTranslated(null);
    setTranslateError("");
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => {
      if (alive) setDetail(ok ? data : null);
    });
    return () => { alive = false; };
  }, [viewId, clientId]);

  const submit = async () => {
    setError("");
    setFileError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "transcription");
    form.append("original", audio);
    form.append("language", language);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) {
      if (data?.original) return setFileError(data.original);
      return setError(errorMessage(data));
    }
    setViewId(null);
    add(data);
  };

  const paragraphs = useMemo(() => (detail?.segments?.length ? detail.segments : (detail?.transcript || "").split("\n\n").map((text) => ({ start: null, text }))), [detail]);
  const plain = () => paragraphs.map((p) => p.text).join("\n\n");
  const withStamps = () => paragraphs.map((p) => (p.start !== null ? `[${stamp(p.start)}] ${p.text}` : p.text)).join("\n\n");
  const words = detail?.transcript ? detail.transcript.trim().split(/\s+/).length : 0;
  const spoken = detail?.detected_language && detail.detected_language !== "auto" ? detail.detected_language.split("-")[0] : "";
  const rtl = RTL_LANGUAGES.includes(spoken);
  // The backup model (Qwen3-ASR) hears Urdu correctly but writes it in the Hindi script (Devanagari).
  const devanagari = (detail?.transcript?.match(/[ऀ-ॿ]/g) || []).length > (detail?.transcript?.length || 0) * 0.3;
  const urduInHindiScript = devanagari && (detail?.language === "ur" || detail?.detected_language === "ur");

  const translate = async () => {
    setTranslating(true);
    setTranslateError("");
    const texts = paragraphs.map((p) => p.text);
    const out = [];
    // The server translates up to 40 paragraphs per request.
    for (let i = 0; i < texts.length; i += 30) {
      const { ok, data } = await apiRequest("/core/transcribe/translate/", { method: "POST", body: { paragraphs: texts.slice(i, i + 30), target } });
      if (!ok) {
        setTranslating(false);
        return setTranslateError(data?.detail || errorMessage(data));
      }
      out.push(...data.paragraphs);
    }
    setTranslating(false);
    setTranslated({ target, paragraphs: out });
  };

  const baseName = (detail?.original_name || "transcript").replace(/\.[^.]+$/, "");
  const targetRtl = translated && RTL_LANGUAGES.includes(translated.target.split("-")[0]);

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Audio and Podcast Transcription</h2>
      <p className="text-center text-gray-400 max-w-2xl mx-auto mb-10">
        Turn a recording - a podcast, interview, lecture, meeting or voice note - into text. Get timestamps, copy or download
        the transcript, and translate it into another language.
      </p>

      <div className="grid md:grid-cols-[1fr_320px] gap-6 mb-6 items-start">
        <UploadCard
          step={1} kind="podcast" icon={FiMic} title="Your recording"
          help="Audio up to 30 minutes. For best results use a clear recording with little background noise."
          picked={audio} onPick={setAudio} onClear={() => setAudio(null)} problem={fileError}
          maxBytes={MAX_AUDIO_BYTES}
        />
        <div className="rounded-2xl bg-white/5 border border-navy-border p-6">
          <label className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3 block" htmlFor="stt-language">Language spoken</label>
          <select id="stt-language" className={selectClass} value={language} onChange={(e) => setLanguage(e.target.value)}>
            {LANGUAGES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
          <p className="text-xs text-gray-500 mt-3">
            {language === "auto"
              ? "The AI model finds out which language is spoken."
              : language === "ur"
                ? "Uses the Whisper AI model, which writes Urdu in Urdu script. If its free GPU time is used up, a backup model is used and writes the Urdu words in Devanagari (Hindi script)."
                : language === "pa"
                  ? "Uses the Whisper AI model. Its free GPU time is limited, so Punjabi can be unavailable at busy times."
                  : "Uses a fast AI model that does not need GPU time."}
          </p>
        </div>
      </div>

      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mb-6">{error}</p>}

      <button
        onClick={submit}
        disabled={!canSubmit}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold text-base shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? <><FiLoader className="animate-spin" /> Uploading...</> : running ? <><FiLoader className="animate-spin" /> Transcribing...</> : <><FiFileText /> Transcribe</>}
      </button>
      {!canSubmit && !running && !submitting && <p className="text-xs text-gray-500 text-center mt-2">Add your recording first</p>}

      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex items-center gap-3">
            <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
            <div>
              <p className="font-semibold">
                Transcribing... {formatSeconds(elapsed)}
                {running.progress && <span className="text-gray-400 font-normal"> · part {running.progress.replace("/", " of ")}</span>}
              </p>
              <p className="text-sm text-gray-400">A 10-minute recording usually takes a few minutes. You can stay on this page.</p>
            </div>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 mt-4 overflow-hidden">
            {running.progress ? (
              <div className="h-full rounded-full bg-brand-gradient transition-all" style={{ width: `${(Number(running.progress.split("/")[0]) / Number(running.progress.split("/")[1])) * 100}%` }} />
            ) : (
              <div className="h-full w-1/3 rounded-full bg-brand-gradient animate-pulse" />
            )}
          </div>
        </div>
      )}

      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>{latest.error_message || "The recording could not be transcribed. Please try again."}</span>
        </div>
      )}

      {detail && detail.status === "completed" && !running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Transcript</p>
              <p className="font-semibold truncate">{detail.original_name}</p>
              <p className="text-xs text-gray-400 mt-1">
                {words.toLocaleString()} words · {formatSeconds(detail.duration_seconds)} long
                {spoken && <> · {LANGUAGE_NAME[spoken] || spoken}</>}
                {detail.engine && <> · by {detail.engine}</>}
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
              <input type="checkbox" checked={timestamps} onChange={(e) => setTimestamps(e.target.checked)} className="accent-accent-light" />
              Show timestamps
            </label>
          </div>

          {urduInHindiScript && (
            <div className="mb-4 rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-100">
              This Urdu speech was written in <b>Devanagari</b> (the Hindi script). The words are what was said, but the
              best Urdu model (Whisper, which writes Urdu script) had no free GPU time, so a backup model was used.
              Try again later for Urdu script, or use <b>Translate &rarr; Urdu</b> below to get Urdu script (a translation, so a
              few words may change).
            </div>
          )}

          <div className="rounded-xl bg-navy border border-navy-border p-5 max-h-[460px] overflow-y-auto space-y-4" dir={rtl ? "rtl" : "ltr"}>
            {paragraphs.map((p, i) => (
              <p key={i} className="text-gray-200 leading-relaxed">
                {timestamps && p.start !== null && <span className="text-accent-light text-xs font-mono mr-2 select-none" dir="ltr">[{stamp(p.start)}]</span>}
                {p.text}
              </p>
            ))}
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <CopyButton getText={() => (timestamps ? withStamps() : plain())} label="Copy transcript" />
            <button
              type="button"
              onClick={() => download(`${baseName}.txt`, timestamps ? withStamps() : plain())}
              className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors"
            >
              <FiDownload /> Download .txt
            </button>
          </div>

          <div className="mt-6 pt-5 border-t border-navy-border">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2"><FiGlobe className="text-accent-light" /> Translate the transcript</p>
            <div className="flex flex-wrap items-center gap-3">
              <select className={`${selectClass} !w-auto min-w-[180px]`} value={target} onChange={(e) => setTarget(e.target.value)} aria-label="Translate to">
                {TRANSLATE_TO.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </select>
              <button
                type="button"
                onClick={translate}
                disabled={translating}
                className="px-5 py-2 rounded-full bg-brand-gradient text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60"
              >
                {translating ? <><FiLoader className="animate-spin" /> Translating...</> : "Translate"}
              </button>
            </div>
            {translateError && <p className="text-sm text-red-400 mt-3">{translateError}</p>}
            {translated && (
              <div className="mt-4">
                <div className="rounded-xl bg-navy border border-navy-border p-5 max-h-[360px] overflow-y-auto space-y-4" dir={targetRtl ? "rtl" : "ltr"}>
                  {translated.paragraphs.map((text, i) => <p key={i} className="text-gray-200 leading-relaxed">{text}</p>)}
                </div>
                <div className="flex flex-wrap gap-3 mt-3">
                  <CopyButton getText={() => translated.paragraphs.join("\n\n")} label="Copy translation" />
                  <button
                    type="button"
                    onClick={() => download(`${baseName}-${translated.target}.txt`, translated.paragraphs.join("\n\n"))}
                    className="px-4 py-2 rounded-full border border-white/20 hover:border-accent-light text-sm font-medium inline-flex items-center gap-2 transition-colors"
                  >
                    <FiDownload /> Download .txt
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-3">Machine translation can miss nuance - check important text.</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-10">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your transcripts</p>
        {loading ? (
          <p className="text-sm text-gray-400">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing here yet. Your transcripts will appear here.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {history.map((job) => (
              <div key={job.id} className={`rounded-xl bg-white/5 border p-4 ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                <p className="text-sm text-gray-200 truncate">{job.original_name || "Recording"}</p>
                <p className="text-xs text-gray-500 mb-3">
                  {new Date(job.created_at).toLocaleString()} · {job.status === "completed" ? `Ready${job.duration_seconds ? ` · ${formatSeconds(job.duration_seconds)}` : ""}` : job.status === "failed" ? "Failed" : "Working..."}
                </p>
                <div className="flex items-center justify-between gap-3">
                  {job.status === "completed" && job.has_transcript ? (
                    <button type="button" onClick={() => setViewId(job.id)} className="text-sm text-accent-light hover:underline">View transcript</button>
                  ) : job.status === "failed" ? (
                    <p className="text-xs text-red-300">{job.error_message}</p>
                  ) : <span />}
                  {job.status !== "processing" && job.status !== "queued" && (
                    <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400 shrink-0"><FiTrash2 size={16} /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
